import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Wine, NotebookPen, Trophy, Compass, MapPin, ShoppingBag, Menu, X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { useEvent } from '../context/EventContext';
import { LanguageSelector } from './LanguageSelector';

const NAV = [
  { to: '/taste', icon: Wine, key: 'nav_taste', module: 'taste' },
  { to: '/notes', icon: NotebookPen, key: 'nav_notes', module: 'notes' },
  { to: '/challenge', icon: Trophy, key: 'nav_challenge', module: 'quiz' },
  { to: '/discover', icon: Compass, key: 'nav_discover', module: 'discover' },
  { to: '/reims', icon: MapPin, key: 'nav_reims', module: 'reims' },
  { to: '/order', icon: ShoppingBag, key: 'nav_order', module: 'order' },
];

export function Layout() {
  const { t } = useLang();
  const { config } = useEvent();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const modules = config?.settings?.modules || {};
  const items = NAV.filter((n) => modules[n.module] !== false);
  const brandName = config?.brand?.short_name || 'Pommery';

  return (
    <div className="app-frame pb-10 min-h-screen">
      <header className="sticky top-0 z-40 bg-[#070C1E]/92 backdrop-blur-md border-b border-[#D4AF37]/20 px-4 py-3 flex justify-between items-center">
        <NavLink to="/" className="font-serif font-bold text-[#E5C158] text-lg tracking-wide" data-testid="header-brand">
          {brandName}
          <span className="text-slate-500 font-sans font-normal text-xs ml-2">× BCFL</span>
        </NavLink>
        <button
          data-testid="menu-toggle"
          aria-label="Menu"
          onClick={() => setOpen(true)}
          className="w-11 h-11 flex items-center justify-center rounded-xl border border-[#D4AF37]/35 text-[#E5C158] active:scale-95 transition-all"
        >
          <Menu size={22} />
        </button>
      </header>

      <main key={location.pathname} className="fade-up px-4 py-5">
        <Outlet />
      </main>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="menu-overlay">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-72 max-w-[82%] bg-[#0B132B] border-l border-[#D4AF37]/25 p-5 flex flex-col slide-in-right shadow-2xl" data-testid="menu-panel">
            <div className="flex justify-between items-center mb-6">
              <span className="font-mono text-[10px] tracking-widest uppercase text-slate-400">{brandName} × BCFL</span>
              <button data-testid="menu-close" onClick={() => setOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-300 active:scale-95">
                <X size={22} />
              </button>
            </div>

            <nav className="flex flex-col gap-1.5">
              {items.map(({ to, icon: Icon, key }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-${key.replace('nav_', '')}-link`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3.5 rounded-xl text-base transition-all ${
                      isActive ? 'bg-gradient-to-r from-[#D4AF37] to-[#E5C158] text-[#070C1E] font-semibold' : 'text-slate-200 hover:bg-[#18244D]'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={20} strokeWidth={isActive ? 2.4 : 1.8} />
                      <span>{t(key)}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="mt-auto pt-6 border-t border-[#D4AF37]/15">
              <p className="font-mono text-[10px] tracking-widest uppercase text-slate-500 mb-3">{t('chooseLanguage')}</p>
              <LanguageSelector variant="welcome" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
